import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Rocket, Play, Calendar, BarChart3, GitBranch, ArrowRight } from 'lucide-react';
import { useSandboxMode } from '../hooks/useSandboxMode';
import { ROUTES } from '../../../shared/utils/constants';

const STORAGE_KEY = 'sandbox_welcome_dismissed';

/**
 * Welcome modal shown to sandbox users on their first login.
 * Explains what they can do and how to upgrade.
 */
export default function SandboxWelcomeModal() {
  const isSandbox = useSandboxMode();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isSandbox && !localStorage.getItem(STORAGE_KEY)) {
      setIsOpen(true);
    }
  }, [isSandbox]);

  if (!isOpen) return null;

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  const handleCreateOrg = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
    navigate(ROUTES.ORGANIZATION_SIGNUP);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header gradient */}
        <div className="relative px-8 pt-8 pb-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1 rounded-lg text-muted hover:text-foreground hover:bg-card transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Welcome to Imago Sandbox</h2>
              <p className="text-sm text-muted">Explore the platform with sample data</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <p className="text-sm text-foreground-secondary mb-5">
            You have access to pre-loaded APIs and orchestrations to try out the core features:
          </p>

          <div className="space-y-3">
            <Feature icon={Play} text="Run APIs" desc="Execute GET & POST requests against live public APIs" />
            <Feature icon={Calendar} text="Schedule Tests" desc="Set up recurring test schedules for any API" />
            <Feature icon={BarChart3} text="View Results" desc="Check test logs, response times, and load test metrics" />
            <Feature icon={GitBranch} text="Try Orchestrations" desc="Run multi-step API workflows and advanced flows" />
          </div>

          <div className="mt-6 p-3 rounded-lg bg-card border border-border">
            <p className="text-xs text-muted">
              Some features like environments, mock servers, and AI tools are available when you create your own organization.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 flex gap-3">
          <button
            onClick={handleDismiss}
            className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-card transition-colors"
          >
            Start Exploring
          </button>
          <button
            onClick={handleCreateOrg}
            className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-hover transition-colors flex items-center justify-center gap-2"
          >
            Create Organization
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{text}</p>
        <p className="text-xs text-muted">{desc}</p>
      </div>
    </div>
  );
}
